import React, { useState, useEffect, useRef } from 'react'
import * as dayjs from 'dayjs'
import moment from 'moment'
import {
  Button,
  Checkbox,
  FileUpload,
  Loading,
  useTraversal,
} from '@guillotinaweb/react-gmi'
import { Alert, Select, Affix, DatePicker as Time, Input, Radio } from 'antd'
import { SyncOutlined, CheckCircleTwoTone } from '@ant-design/icons'
import { message } from 'antd'

import { BaseField } from '../../../common/field'
import { DatePicker } from '../../../common/date'
import { RegionSelector } from '../../../components/region-selector'
import { LANGS, SELLING_COUNTRIES, ALL_COUNTRIES } from '../../../consts'
import { endOfTomorrow } from 'date-fns'
import { toISOStringWithTimezone } from '../../../utils'

const right = {
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
}

const tomorrow = toISOStringWithTimezone(endOfTomorrow())

export function Schedule() {
  const Ctx = useTraversal()
  const [count, setCount] = useState({})
  const [saving, setSaving] = useState(false)
  const [state, setState] = useState(Ctx.state.context)
  const [loading, setLoading] = useState(false)
  const firstRender = useRef(true)
  const [lastSave, setLastSave] = useState(state.modification_date)
  const [progress, setProgress] = useState({})
  const [displayB, setDisplayB] = useState(Boolean(state.schedule_at_b))
  const [colectivos, setColectivos] = useState({})

  const status = state.status || 'draft'
  const scheduled = status === 'scheduled'
  const printVal = (v) => (v === undefined ? <SyncOutlined spin /> : v)
  let btnLabel = 'SCHEDULE'

  if (loading && !scheduled) btnLabel = 'Scheduling...'
  else if (loading && scheduled) btnLabel = 'Unscheduling...'
  else if (scheduled) btnLabel = 'UNSCHEDULE'

  const sendtoSource = count?.source

  useEffect(() => {
    getColectivos()
  }, [])

  async function getColectivos() {
    const res = await Ctx.client.getColectivos(state.id)
    res.map((val) => (colectivos[val.id] = val.name))
    setColectivos(colectivos)
  }

  useEffect(onMount, [])
  function onMount() {
    let closeMsg
    const missing = Object.entries(state.subject).filter(([k, v]) => !v)

    if (missing.length > 0) {
      const langs = missing.map(([k]) => k.toUpperCase()).join(', ')

      closeMsg = message.warning({
        content: `The subject is missing in: ${langs}`,
        key: 'subject-error',
        duration: 0, // infinite
      })
    }

    return () => closeMsg && closeMsg()
  }

  useEffect(() => {
    if (firstRender.current) {
      setCount({ ...count, loading: true })
      firstRender.current = false
      Ctx.client
        .countNewsletter(state.id)
        .then((r) => setCount({ ...r, loading: false }))
      return
    }

    const save = () => {
      setCount({ ...count, loading: true })
      Ctx.client
        .patch(Ctx.path, {
          sendto: state.sendto,
          schedule_at_b: state.schedule_at_b,
          schedule_at: state.schedule_at,
          finish_at: state.finish_at,
          finish_at_b: state.finish_at_b,
        })
        .then((r) => {
          if (r.status !== 204) throw new Error('Error saving the newsletter')
          setLastSave(new Date().toISOString())
          setSaving(false)
          return Ctx.client.countNewsletter(state.id)
        })
        .then((r) => setCount({ ...r, loading: false }))
        .catch((e) => message.error(e.message))
    }

    setSaving(true)
    // Debounce save
    const timeout = setTimeout(save, 150)
    return () => clearTimeout(timeout)
  }, [state])

  useEffect(calculateProgress, [scheduled, state])
  function calculateProgress() {
    let unmounted = false
    const { sent, nosent, norendered } = progress
    const retry = () => {
      if (unmounted) return
      timeout = setTimeout(calculateProgress, 2000)
    }
    let timeout

    if (!state.id || !scheduled || sent + nosent + norendered === count.total)
      return

    Ctx.client.progressNL(state.id).then(setProgress).then(retry).catch(retry)

    return () => {
      unmounted = true
      clearTimeout(timeout)
    }
  }

  function schedule() {
    setLoading(true)
    Ctx.client
      .scheduleNewsletter(state.id, scheduled)
      .then((r) => {
        if (r.message) throw new Error()
        setLoading(false)
        message.success(scheduled ? 'Unscheduled!' : 'Scheduled!')
        Ctx.refresh()
      })
      .catch((e) => {
        setLoading(false)
        message.error(
          scheduled
            ? 'Error unscheduling the newsletter'
            : 'Error scheduling the newsletter'
        )
      })
  }

  function uploadToBrevo() {
    setLoading(true)
    Ctx.client
      .uploadToBrevo(state.id)
      .then((r) => {
        setLoading(false)
        if (r.status === 200 || r.status === 201) {
          message.success(r.message)
          Ctx.refresh()
        } else {
          message.error(`Error uploading to Brevo! Status: ${r.status}`)
        }
      })
      .catch((e) => {
        setLoading(false)
        message.error('Error uploading to Brevo!')
      })
  }

  function setTimeToDate(date, time) {
    if (!date) return null

    const newDate = new Date(date)
    newDate.setHours(time.getHours())
    newDate.setMinutes(time.getMinutes())
    newDate.setSeconds(time.getSeconds())
    return newDate
  }

  function ScheduleAt({ label, AB = 'a' }) {
    const field = AB === 'b' ? 'schedule_at_b' : 'schedule_at'
    const finishField = AB === 'b' ? 'finish_at_b' : 'finish_at'

    return (
      <>
        <BaseField label={label}>
          <DatePicker
            disabled={scheduled}
            value={state[field] || tomorrow}
            onUpdate={(e) => {
              const currentDate = new Date(state[field] || tomorrow)
              const newDay = new Date(e.target.value)
              const date = setTimeToDate(newDay, currentDate)

              // Update finish_at as well
              const finishState = {}
              let finishDate = state[finishField]
              if (finishDate) {
                finishDate = setTimeToDate(date, new Date(finishDate))
                finishState[finishField] = toISOStringWithTimezone(finishDate)
              }

              setState((s) => ({
                ...s,
                ...finishState,
                [field]: toISOStringWithTimezone(date),
              }))
            }}
          />
        </BaseField>
        <BaseField label="Start">
          <Time
            disabled={scheduled}
            picker="time"
            value={moment(new Date(state[field] || tomorrow))}
            onChange={(e) => {
              const d = new Date(e.toString())
              const date = setTimeToDate(state[field], d)
              setState((s) => ({
                ...s,
                [field]: toISOStringWithTimezone(date),
              }))
            }}
          />
        </BaseField>
        <BaseField label="End">
          <Time
            disabled={scheduled}
            picker="time"
            value={
              state[finishField]
                ? moment(new Date(state[finishField]))
                : undefined
            }
            onChange={(e) => {
              const d = new Date(e.toString())
              const date = setTimeToDate(state[field], d)
              setState((s) => ({
                ...s,
                [finishField]: toISOStringWithTimezone(date),
              }))
            }}
          />
        </BaseField>
      </>
    )
  }

  return (
    <div className="nl-schedule">
      <Affix offsetTop={10}>
        <div className="last-edition">
          {saving ? (
            <SyncOutlined spin />
          ) : (
            <CheckCircleTwoTone twoToneColor="#52c41a" />
          )}{' '}
          Last saved: {dayjs(lastSave).format('DD/MM/YYYY HH:mm:ss')}
        </div>
        <div className="newsletter-controls">
          <div>
            <b>Total audience: </b>
            {printVal(count.total)} (source: {count.source})
          </div>
          {scheduled && (
            <div className="sending-status">
              <span>
                <b>No rendered:</b>
                {printVal(progress.norendered)}
              </span>
              <span>
                <b>No sent:</b>
                {printVal(progress.nosent)}
              </span>
              <span>
                <b>Pending:</b>
                {printVal(progress.pending)}
              </span>
              <span>
                <b>Rendered:</b>
                {printVal(progress.rendered)}
              </span>
              <span>
                <b>Sent:</b>
                {printVal(progress.sent)}
              </span>
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <Button
              onClick={schedule}
              disabled={
                loading ||
                status === 'draft' ||
                !Ctx.state.permissions['vinissimus.ScheduleNewsletters']
              }
              className="button is-normal is-primary"
              style={{ minWidth: '120px' }}
            >
              {btnLabel}
            </Button>

            <Button
              onClick={uploadToBrevo}
              disabled={
                loading ||
                !Ctx.state.permissions['vinissimus.ScheduleNewsletters']
              }
              className="button is-normal is-success"
              style={{ minWidth: '120px' }}
            >
              Subir a Brevo
            </Button>
          </div>

          {sendtoSource !== undefined && count.loading === true && <Loading />}
        </div>
      </Affix>

      <BaseField className="control text" label="Status">
        {status}
      </BaseField>

      <ScheduleAt label="Schedule at" />
      <div style={{ textAlign: 'right' }}>
        <Checkbox
          disabled={scheduled}
          children=" Choose another schedule date for test A/B"
          value={displayB}
          onChange={(v) => {
            setState((s) => ({
              ...s,
              schedule_at_b: v ? s.schedule_at : null,
              finish_at_b: v ? s.finish_at_b : null,
            }))
            setDisplayB(v)
          }}
        />
      </div>
      {displayB && <ScheduleAt label="Schedule at (B)" AB="b" />}

      {sendtoSource === undefined ? (
        <Loading />
      ) : (
        <>
          <SendToEmails
            isActive={sendtoSource === 'list_emails'}
            scheduled={scheduled}
            state={state}
            count={count}
            setCount={setCount}
          />

          <hr />

          <AudienceConditions
            isActive={sendtoSource === 'filters'}
            scheduled={scheduled}
            state={state}
            setState={setState}
            colectivos={colectivos}
          />
        </>
      )}
    </div>
  )
}

function SendToEmails({ isActive, count, setCount, state, scheduled }) {
  const Ctx = useTraversal()

  const uploadEmails = (emails) => {
    setCount({ ...count, loading: true })
    Ctx.client.rest
      .request(
        Ctx.path + '/@sendto-emails',
        {
          method: 'POST',
          body: emails,
        },
        Ctx.client.rest.getHeaders()
      )
      .then((r) => {
        if (r.status !== 200) throw new Error('Error saving the newsletter')
        return Ctx.client
          .countNewsletter(state.id)
          .then((r) => setCount({ ...r, loading: false }))
      })
      .catch(() => ({}))
  }

  return (
    <>
      <div style={{ margin: '35px 0 15px 0' }}>
        <span
          style={{
            fontSize: '18px',
            fontWeight: 'bold',
            textTransform: 'uppercase',
          }}
        >
          Audience emails
        </span>
      </div>

      <BaseField label="List of emails">
        {!isActive && (
          <FileUpload
            disabled={scheduled}
            onChange={(f) =>
              uploadEmails(new TextDecoder('utf-8').decode(f.data))
            }
          />
        )}
        {isActive && (
          <Button
            className="button"
            disabled={scheduled}
            onClick={() => uploadEmails('')}
          >
            Delete emails filter
          </Button>
        )}

        <div style={{ marginTop: '20px' }}>
          <Alert
            message="Upload a txt file with a list of email addresses (one email per line)"
            type="info"
            showIcon
          />
        </div>
      </BaseField>
    </>
  )
}

function AudienceConditions({
  isActive,
  state,
  setState,
  scheduled,
  colectivos,
}) {
  const Ctx = useTraversal()

  const disabled = scheduled || !isActive

  const FREQUENCIES = {
    1000: '∞ email/week (users without a limit of emails per week)',
    1: '≥ 1 email/week (users that allow 1 email per week or more)',
    // 0: "Everyone (doesn't exist yet)"
  }

  const [fieldConsts, setFieldConsts] = useState({
    provincias: [],
    paises: [],
  })

  const setPreferences = (field, v) =>
    setState((s) => ({
      ...s,
      sendto: {
        ...s.sendto,
        [field]: v,
      },
    }))

  const setOpPreferences = (field, newVal) =>
    setState((s) => ({
      ...s,
      sendto: {
        ...s.sendto,
        [field]: {
          op: s.sendto[field]?.op || null,
          value: s.sendto[field]?.value || null,
          ...newVal,
        },
      },
    }))

  useEffect(() => {
    async function load() {
      const r = await Ctx.client.rest.get('@field-constants')
      if (r.status === 200) {
        const data = await r.json()
        setFieldConsts(data)
      } else {
        console.error(r.text())
      }
    }
    load()
  }, [Ctx.path])

  return (
    <>
      <div style={{ margin: '35px 0 15px 0' }}>
        <span
          style={{
            fontSize: '18px',
            fontWeight: 'bold',
            textTransform: 'uppercase',
          }}
        >
          Audience conditions
        </span>

        <BaseField label="">
          {!isActive && (
            <Alert
              message="When a list of emails is provided the following filters are ignored"
              type="warning"
              showIcon
            />
          )}
        </BaseField>
      </div>

      <BaseField label="Languages">
        <Select
          allowClear
          disabled={disabled}
          mode="multiple"
          onChange={(l) => setPreferences('langs', l)}
          placeholder="Please select a language"
          style={{ width: '100%' }}
          value={state.sendto?.langs || []}
        >
          {LANGS.map((lang) => (
            <Select.Option key={lang}>{lang.toUpperCase()}</Select.Option>
          ))}
        </Select>
        <div style={right}>
          <Button
            disabled={disabled}
            className="button is-small"
            onClick={() => setPreferences('langs', [])}
          >
            Unselect all
          </Button>
          <Button
            disabled={disabled}
            className="button is-small"
            onClick={() => setPreferences('langs', LANGS)}
          >
            Select all
          </Button>
        </div>
      </BaseField>
      <BaseField label="Countries">
        <Select
          allowClear
          disabled={disabled}
          mode="multiple"
          onChange={(c) => setPreferences('countries', c)}
          placeholder="Please select a country"
          style={{ width: '100%' }}
          value={state.sendto?.countries || []}
        >
          {SELLING_COUNTRIES.map((c) => (
            <Select.Option key={c}>{`${
              ALL_COUNTRIES[c]
            } (${c.toUpperCase()})`}</Select.Option>
          ))}
        </Select>
        <div style={right}>
          <Button
            className="button is-small"
            disabled={disabled}
            onClick={() => setPreferences('countries', [])}
          >
            Unselect all
          </Button>
          <Button
            className="button is-small"
            disabled={disabled}
            onClick={() => setPreferences('countries', SELLING_COUNTRIES)}
          >
            Select all
          </Button>
        </div>
      </BaseField>
      <BaseField label="Frequency">
        <Select
          allowClear
          disabled={disabled}
          mode="single"
          onChange={(f) => setPreferences('frequency', parseInt(f))}
          placeholder="Please select an audience"
          style={{ width: '100%' }}
          value={FREQUENCIES[state.sendto?.frequency || 1000]}
        >
          {Object.keys(FREQUENCIES).map((f) => (
            <Select.Option key={f}>{FREQUENCIES[f]}</Select.Option>
          ))}
        </Select>
      </BaseField>

      {/* <div style={{ margin: '35px 0 15px 0', textTransform: 'uppercase' }}>
      <span style={{ fontSize: '18px', fontWeight: 'bold' }}>
        Advanced audience conditions
      </span>
    </div> */}

      <BaseField label="Province" style={{ maxWidth: '375px' }}>
        <Select
          allowClear
          disabled={disabled}
          mode="single"
          onChange={(f) => setPreferences('id_provincia', f || null)}
          placeholder="Please select an audience"
          style={{ width: '100%' }}
          value={state.sendto?.id_provincia}
        >
          {fieldConsts.provincias.map((p) => (
            <Select.Option key={p}>{p}</Select.Option>
          ))}
        </Select>
      </BaseField>

      <BaseField label="City" style={{ maxWidth: '375px' }}>
        <Input
          disabled={disabled}
          onChange={(e) => setPreferences('ciudad', e.target.value || null)}
          style={{ width: '100%' }}
          value={state.sendto?.ciudad}
        />
      </BaseField>

      <BaseField
        label="Avg order frequency (days)"
        style={{ display: 'flex', maxWidth: '375px' }}
      >
        <Input
          allowClear
          disabled={disabled}
          addonBefore={
            <SelectOperation
              disabled={disabled}
              style={{ width: '100px' }}
              ops={{ lt: 'less than', gt: 'more than' }}
              value={state.sendto?.frecuencia?.op}
              onChange={(v) => setOpPreferences('frecuencia', { op: v })}
            />
          }
          onChange={(e) =>
            setOpPreferences('frecuencia', {
              op: state.sendto?.frecuencia?.op || 'lt',
              value: e.target.value || null,
            })
          }
          value={state.sendto?.frecuencia?.value}
        />
      </BaseField>

      <BaseField
        label="Num orders"
        style={{ display: 'flex', maxWidth: '375px' }}
      >
        <Input
          allowClear
          disabled={disabled}
          addonBefore={
            <SelectOperation
              disabled={disabled}
              style={{ width: '100px' }}
              ops={{ lt: 'less than', gt: 'more than' }}
              value={state.sendto?.num_pedidos?.op || 'lt'}
              onChange={(v) => setOpPreferences('num_pedidos', { op: v })}
            />
          }
          onChange={(e) =>
            setOpPreferences('num_pedidos', {
              op: state.sendto?.num_pedidos?.op || 'lt',
              value: e.target.value || null,
            })
          }
          value={state.sendto?.num_pedidos?.value}
        />
      </BaseField>

      <BaseField label="Order total">
        <Input
          allowClear
          disabled={disabled}
          style={{ maxWidth: '375px' }}
          addonBefore={
            <SelectOperation
              disabled={disabled}
              style={{ width: '100px' }}
              ops={{ lt: 'less than', gt: 'more than' }}
              value={state.sendto?.importe_total_pds?.op || 'lt'}
              onChange={(v) => setOpPreferences('importe_total_pds', { op: v })}
            />
          }
          addonAfter={'€'}
          onChange={(e) =>
            setOpPreferences('importe_total_pds', {
              op: state.sendto?.importe_total_pds?.op || 'lt',
              value: e.target.value || null,
            })
          }
          value={state.sendto?.importe_total_pds?.value}
        />
      </BaseField>

      <BaseField label="Average bottle price">
        <Input
          allowClear
          disabled={disabled}
          style={{ maxWidth: '375px' }}
          addonBefore={
            <SelectOperation
              disabled={disabled}
              style={{ width: '100px' }}
              ops={{ lt: 'less than', gt: 'more than' }}
              value={state.sendto?.avg_price_bot?.op || 'lt'}
              onChange={(v) => setOpPreferences('avg_price_bot', { op: v })}
            />
          }
          addonAfter={'€'}
          onChange={(e) =>
            setOpPreferences('avg_price_bot', {
              op: state.sendto?.avg_price_bot?.op || 'lt',
              value: e.target.value || null,
            })
          }
          value={state.sendto?.avg_price_bot?.value}
        />
      </BaseField>

      <BaseField label="Maximum bottle price">
        <Input
          allowClear
          disabled={disabled}
          style={{ maxWidth: '375px' }}
          addonBefore={
            <SelectOperation
              disabled={disabled}
              style={{ width: '100px' }}
              ops={{ lt: 'less than', gt: 'more than' }}
              value={state.sendto?.max_price_bot?.op || 'lt'}
              onChange={(v) => setOpPreferences('max_price_bot', { op: v })}
            />
          }
          addonAfter={'€'}
          onChange={(e) =>
            setOpPreferences('max_price_bot', {
              op: state.sendto?.max_price_bot?.op || 'lt',
              value: e.target.value || null,
            })
          }
          value={state.sendto?.max_price_bot?.value}
        />
      </BaseField>

      <BaseField label="Favourite region" style={{ maxWidth: '375px' }}>
        <RegionSelector
          allowClear
          map={(m) => ({
            id: m.id.replace('/zonas', ''),
            name: m.title || m.id,
          })}
          value={state.sendto?.fav_region}
          onChange={(v) => setPreferences('fav_region', v || null)}
          loadRegions={searchRegions}
          disabled={disabled}
        />
      </BaseField>

      <BaseField label="Colectivo type">
        <Select
          allowClear
          disabled={disabled}
          mode="multiple"
          onChange={(c) => setPreferences('colectivos_id', c)}
          placeholder="Please select a colectivo"
          style={{ width: '100%' }}
          value={state.sendto?.colectivos_id || []}
        >
          {Object.keys(colectivos).map((c) => (
            <Select.Option key={c}>{colectivos[c]}</Select.Option>
          ))}
        </Select>
        <div style={right}>
          <Button
            className="button is-small"
            disabled={disabled}
            onClick={() => setPreferences('colectivos_id', [])}
          >
            Unselect all
          </Button>
          <Button
            className="button is-small"
            disabled={disabled}
            onClick={() =>
              setPreferences('colectivos_id', Object.keys(colectivos))
            }
          >
            Select all
          </Button>
        </div>
      </BaseField>

      <BaseField label="First order placed">
        <Input
          allowClear
          disabled={disabled}
          style={{ maxWidth: '375px' }}
          placeholder={'2019-02-25'}
          addonBefore={
            <SelectOperation
              disabled={disabled}
              style={{ width: '100px' }}
              ops={{ lt: 'before', gt: 'after' }}
              value={state.sendto?.fecha_first_pd?.op || 'lt'}
              onChange={(v) => setOpPreferences('fecha_first_pd', { op: v })}
            />
          }
          onChange={(e) => {
            if (e.target.value === '') {
              setPreferences('fecha_first_pd', null)
            } else {
              setOpPreferences('fecha_first_pd', {
                op: state.sendto?.fecha_first_pd?.op || 'lt',
                value: e.target.value,
              })
            }
          }}
          value={state.sendto?.fecha_first_pd?.value}
        />
      </BaseField>

      <BaseField label="Last order placed">
        <Input
          allowClear
          disabled={disabled}
          style={{ maxWidth: '375px' }}
          placeholder={'2022-02-25'}
          addonBefore={
            <SelectOperation
              disabled={disabled}
              style={{ width: '100px' }}
              ops={{ lt: 'before', gt: 'after' }}
              value={state.sendto?.fecha_last_pd?.op || 'lt'}
              onChange={(v) => setOpPreferences('fecha_last_pd', { op: v })}
            />
          }
          onChange={(e) => {
            if (e.target.value === '') {
              setPreferences('fecha_last_pd', null)
            } else {
              setOpPreferences('fecha_last_pd', {
                op: state.sendto?.fecha_last_pd?.op || 'lt',
                value: e.target.value,
              })
            }
          }}
          value={state.sendto?.fecha_last_pd?.value}
        />
      </BaseField>
    </>
  )
}

function SelectOperation({ ops, value, onChange, ...props }) {
  return (
    <Select
      className="select-before"
      value={value}
      onChange={onChange}
      {...props}
    >
      {Object.keys(ops).map((k) => (
        <Select.Option value={k}>{ops[k]}</Select.Option>
      ))}
    </Select>
  )
}

export async function searchRegions() {
  const obj = await fetch(`/db/guillotina/@list-id-zonas?lang=es`).then((res) =>
    res.json()
  )
  return obj?.zonas
    ?.filter((item) => item.path.split('/').length - 1 === 2)
    ?.map?.((item) => ({ id: item.path, name: item.name }))
}
